/* BIÉDE Japan default currency session preference fix v2026-09-15-1 */
if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('[data-country-filter]'),
          reset: this.querySelector('[data-country-filter-reset]'),
          status: this.querySelector('[data-country-filter-status]'),
          empty: this.querySelector('[data-country-filter-empty]'),
          divider: this.querySelector('[data-country-divider]'),
        };

        this.countryItems = Array.from(this.querySelectorAll('[data-country-item]'));
        this.countryGroups = Array.from(this.querySelectorAll('[data-country-group]'));
        this.header = this.closest('.header-wrapper');
        this.onDocumentClickHandler = this.onDocumentClick.bind(this);

        if (!this.elements.input || !this.elements.button || !this.elements.panel) return;

        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        document.addEventListener('click', this.onDocumentClickHandler);

        this.querySelectorAll('a[data-value]').forEach((item) => {
          item.addEventListener('click', this.onItemClick.bind(this));
        });

        if (this.elements.search) {
          this.elements.search.addEventListener('input', this.filterCountries.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }

        if (this.elements.reset) {
          this.elements.reset.addEventListener('click', this.resetFilter.bind(this));
          this.elements.reset.addEventListener('mousedown', (event) => event.preventDefault());
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClickHandler);
      }

      hidePanel() {
        if (!this.elements.button || !this.elements.panel) return;

        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
        }

        if (this.header && this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = false;
        }
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (this.elements.button.getAttribute('aria-expanded') === 'false') return;

        event.preventDefault();
        event.stopPropagation();
        this.hidePanel();
        this.elements.button.focus();
      }

      onContainerKeyDown(event) {
        if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
        if (!['ARROWUP', 'ARROWDOWN'].includes(event.code.toUpperCase())) return;

        const links = this.visibleLinks();
        if (!links.length) return;

        const currentIndex = links.indexOf(document.activeElement);
        let nextIndex;

        if (event.code.toUpperCase() === 'ARROWDOWN') {
          nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
        }

        event.preventDefault();
        links[nextIndex].focus();

      }

      onItemClick(event) {
        event.preventDefault();

        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;

        if (this.elements.input.name === 'country_code') {
          try {
            const selectedCountry = event.currentTarget.dataset.value || '';
            const manualMarketStorageKey = 'biede_manual_market_selection_session';
            if (/^[A-Z]{2}$/.test(selectedCountry) && selectedCountry !== 'JP') {
              window.sessionStorage.setItem(manualMarketStorageKey, selectedCountry);
            } else {
              window.sessionStorage.removeItem(manualMarketStorageKey);
            }
          } catch (error) {
            // Shopify's localization form still works when storage is unavailable.
          }
        }

        if (form) form.submit();
      }

      openSelector() {
        const isOpening = this.elements.panel.hasAttribute('hidden');

        if (!isOpening) {
          this.hidePanel();
          return;
        }

        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');

        if (this.header && this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }

        if (this.elements.search && window.matchMedia('(min-width: 750px)').matches) {
          requestAnimationFrame(() => this.elements.search.focus());
        }
      }

      closeSelector(event) {
        if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
          this.hidePanel();
        }
      }

      onDocumentClick(event) {
        if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
        if (!this.contains(event.target)) this.hidePanel();
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') event.preventDefault();
      }

      normalizeString(value) {
        const stringValue = String(value || '');
        const normalizedValue = typeof stringValue.normalize === 'function' ? stringValue.normalize('NFD') : stringValue;

        return normalizedValue.replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        let visibleCount = 0;

        this.countryItems.forEach((item) => {
          const matches = this.normalizeString(item.dataset.searchTerms).includes(searchValue);
          item.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        this.countryGroups.forEach((group) => {
          group.hidden = !Array.from(group.querySelectorAll('[data-country-item]')).some((item) => !item.hidden);
        });

        if (this.elements.divider) {
          const visibleGroups = this.countryGroups.filter((group) => !group.hidden).length;
          this.elements.divider.hidden = visibleGroups < 2;
        }

        if (this.elements.reset) this.elements.reset.hidden = !searchValue;
        if (this.elements.empty) this.elements.empty.hidden = visibleCount !== 0;

        if (this.elements.status) {
          const countTemplate = window.accessibilityStrings && window.accessibilityStrings.countrySelectorSearchCount;
          this.elements.status.textContent = countTemplate
            ? countTemplate.replace('[count]', visibleCount)
            : String(visibleCount);
        }

        const results = this.querySelector('.biede-localization-selector__list');
        if (results) results.scrollTop = 0;
      }

      resetFilter(event) {
        event.preventDefault();
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      visibleLinks() {
        return Array.from(this.querySelectorAll('a[data-value]')).filter((link) => {
          const item = link.closest('.disclosure__item');
          const group = link.closest('[data-country-group]');
          return (!item || !item.hidden) && (!group || !group.hidden);
        });
      }
    }
  );
}
